import { For, Show, createSignal } from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { api } from "../api/client";
import type { CalendarDay, ExerciseType } from "../api/types";
import { t } from "../i18n";
import { activeSessionSignal } from "../lib/activeWorkout";
import { EXERCISE_TYPES, calendarTypes, rowType, typeLabel } from "../lib/activity";
import { createCachedResource } from "../lib/cache";
import { calendarKey, currentYearMonth } from "../lib/data";
import { formatDuration } from "../lib/duration";
import { withFrom } from "../lib/nav";
import {
  cloneExercises,
  lastWeekRangeISO,
  repeatCompletedHref,
  sameWeekdayThisWeek,
} from "../lib/repeatWorkout";
import { tgAlert, tgConfirm } from "../lib/telegram";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";

const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;

function monthLabel(year: number, month: number, locale: string) {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(
    locale.startsWith("en") ? "en-US" : "ru-RU",
    { month: "long", year: "numeric", timeZone: "UTC" },
  );
}

function buildCells(year: number, month: number) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  let startPad = first.getUTCDay() - 1;
  if (startPad < 0) startPad = 6;

  const cells: Array<{ day: number | null; key: string | null }> = [];
  for (let i = 0; i < startPad; i++) cells.push({ day: null, key: null });
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, key });
  }
  while (cells.length % 7 !== 0) cells.push({ day: null, key: null });
  return cells;
}

/**
 * Dot types of a day. The backend lists the types of completed AND planned
 * workouts; the dots show what was done, so keep only types that occur in the
 * completed ones. Empty = no dots (old backend, no completed workouts).
 */
function doneTypes(day: CalendarDay): ExerciseType[] {
  if (!day.completed.length) return [];
  const done = new Set(day.completed.flatMap((w) => w.exercises.map((e) => rowType(e))));
  return calendarTypes(day.exerciseTypes).filter((x) => done.has(x));
}

export default function CalendarPage() {
  const now = new Date();
  const [year, setYear] = createSignal(currentYearMonth().y);
  const [month, setMonth] = createSignal(currentYearMonth().m);
  const [selected, setSelected] = createSignal<string | null>(
    now.toISOString().slice(0, 10),
  );
  const navigate = useNavigate();
  const [weekBusy, setWeekBusy] = createSignal(false);

  const [calendar, { refetch }] = createCachedResource(
    () => ({ y: year(), m: month() }),
    {
      key: ({ y, m }) => calendarKey(y, m),
      fetch: ({ y, m }) => api.getCalendar(y, m),
    },
  );

  async function repeatLastWeek() {
    if (weekBusy()) return;
    setWeekBusy(true);
    try {
      const { from, to } = lastWeekRangeISO();
      const list = await api.listCompleted(from, to);
      if (list.length === 0) {
        await tgAlert(t("NO_LAST_WEEK_WORKOUTS"));
        return;
      }
      if (
        !(await tgConfirm(
          t("CONFIRM_REPEAT_LAST_WEEK").replace("{n}", String(list.length)),
        ))
      ) {
        return;
      }
      for (const w of list) {
        await api.createPlanned({
          title: w.title,
          notes: null,
          plannedFor: sameWeekdayThisWeek(w.performedAt),
          exercises: cloneExercises(w.exercises),
        });
      }
      await refetch();
    } catch (err) {
      await tgAlert(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setWeekBusy(false);
    }
  }

  function prevMonth() {
    if (month() === 1) {
      setYear(year() - 1);
      setMonth(12);
    } else setMonth(month() - 1);
  }

  function nextMonth() {
    if (month() === 12) {
      setYear(year() + 1);
      setMonth(1);
    } else setMonth(month() + 1);
  }

  const dayData = (): CalendarDay | null => {
    const key = selected();
    if (!key || !calendar()) return null;
    return calendar()!.days[key] ?? { completed: [], planned: [] };
  };

  const monthTypes = () => {
    const days = Object.values(calendar()?.days ?? {}).filter((d) => d.completed.length);
    return EXERCISE_TYPES.filter((type) =>
      days.some((d) => doneTypes(d).includes(type)),
    );
  };

  const todayKey = now.toISOString().slice(0, 10);
  const active = activeSessionSignal();
  const openPlans = (data: CalendarDay | undefined) =>
    data?.planned.filter(
      (p) => p.status !== "completed" && !p.completedWorkoutId,
    ) ?? [];

  return (
    <div class="cal-page">
      <header class="cal-head">
        <button type="button" class="btn-ghost px-2 text-lg" onClick={prevMonth}>
          ‹
        </button>
        <h1 class="page-title">
          {monthLabel(year(), month(), navigator.language)}
        </h1>
        <button type="button" class="btn-ghost px-2 text-lg" onClick={nextMonth}>
          ›
        </button>
        <A
          href="/workouts"
          class="btn-ghost shrink-0 px-2"
          aria-label={t("WORKOUTS_LIST")}
        >
          <ListIcon />
        </A>
      </header>

      <Show when={active()}>
        <Section>
          <Cell
            href="/workouts/active"
            title={active()!.title || t("ACTIVE_RESUME")}
            value={t("ACTIVE_RESUME")}
            chevron
          />
        </Section>
      </Show>

      <div class="card cal-grid-card">
        <div class="cal-weekdays grid grid-cols-7 gap-0.5 text-center font-semibold uppercase text-ink-faint">
          <For each={[...WEEKDAYS]}>{(d) => <div>{t(d)}</div>}</For>
        </div>
        <div class="cal-grid">
          <For each={buildCells(year(), month())}>
            {(cell) => {
              if (!cell.day || !cell.key) {
                return <div />;
              }
              const key = cell.key;
              const data = () => calendar()?.days[key];
              const hasCompleted = () => (data()?.completed.length ?? 0) > 0;
              const types = () => (data() ? doneTypes(data()!) : []);
              const plans = () => openPlans(data());
              const hasPlanned = () =>
                plans().some((p) => p.status !== "missed");
              const hasMissed = () =>
                plans().some((p) => p.status === "missed");
              const isSelected = () => selected() === key;
              const isToday = key === todayKey;

              return (
                <button
                  type="button"
                  class={`cal-day ${
                    isSelected()
                      ? "is-selected"
                      : isToday
                        ? "text-[var(--link)] font-semibold"
                        : "text-ink"
                  } ${hasCompleted() ? "has-done" : ""} ${
                    hasPlanned() ? "has-plan" : ""
                  } ${hasMissed() ? "has-miss" : ""}`}
                  onClick={() => setSelected(key)}
                >
                  {cell.day}
                  <span class="cal-marks">
                    <Show
                      when={types().length}
                      fallback={
                        <Show when={hasCompleted()}>
                          <span class="cal-mark cal-mark--done" />
                        </Show>
                      }
                    >
                      <For each={types()}>
                        {(type) => <span class={`cal-mark cal-mark--${type}`} />}
                      </For>
                    </Show>
                    <Show when={hasPlanned()}>
                      <span class="cal-mark cal-mark--plan" />
                    </Show>
                    <Show when={hasMissed()}>
                      <span class="cal-mark cal-mark--miss" />
                    </Show>
                  </span>
                </button>
              );
            }}
          </For>
        </div>
        <div class="cal-legend">
          <Show
            when={monthTypes().length}
            fallback={
              <span>
                <i class="bg-success" />
                {t("COMPLETED")}
              </span>
            }
          >
            <For each={monthTypes()}>
              {(type) => (
                <span>
                  <i class={`cal-mark--${type}`} />
                  {typeLabel(type)}
                </span>
              )}
            </For>
          </Show>
          <span>
            <i class="bg-planned" />
            {t("PLANNED")}
          </span>
          <span>
            <i class="bg-warning" />
            {t("MISSED")}
          </span>
        </div>
      </div>

      <div class="cal-actions">
        <div class="flex gap-2">
          <button
            type="button"
            class="btn-primary flex-1"
            onClick={() =>
              navigate(`/workouts/new/completed?date=${selected() ?? todayKey}`)
            }
          >
            {t("LOG_WORKOUT")}
          </button>
          <button
            type="button"
            class="btn-secondary flex-1"
            onClick={() =>
              navigate(`/workouts/new/planned?date=${selected() ?? todayKey}`)
            }
          >
            {t("PLAN_WORKOUT")}
          </button>
        </div>
        <Section>
          <Cell
            title={t("REPEAT_LAST")}
            chevron
            onClick={() =>
              navigate(
                repeatCompletedHref({
                  fromLast: true,
                  date: selected() ?? todayKey,
                }),
              )
            }
          />
          <Cell
            title={t("REPEAT_LAST_WEEK")}
            disabled={weekBusy()}
            onClick={() => void repeatLastWeek()}
          />
        </Section>
      </div>

      <div class="cal-day-list">
        <Show when={calendar.loading}>
          <p class="py-4 text-center text-[15px] text-ink-muted">{t("LOADING")}</p>
        </Show>
        <Show when={!calendar.loading && dayData()}>
          {(data) => (
            <Show
              when={
                data().completed.length +
                  data().planned.filter(
                    (p) => p.status !== "completed" && !p.completedWorkoutId,
                  ).length >
                0
              }
              fallback={
                <p class="px-1 py-3 text-center text-[15px] text-ink-muted">
                  {t("EMPTY_DAY")}
                </p>
              }
            >
              <Section>
                <For each={data().completed}>
                  {(w) => (
                    <Cell
                      href={`/workouts/completed/${w.id}`}
                      state={withFrom("/")}
                      title={w.title}
                      subtitle={`${w.exercises.length} ${t("EXERCISES").toLowerCase()}${
                        formatDuration(w.durationMinutes)
                          ? ` · ${formatDuration(w.durationMinutes)}`
                          : ""
                      }`}
                      value={t("COMPLETED")}
                      chevron
                    />
                  )}
                </For>
                <For
                  each={data().planned.filter(
                    (p) => p.status !== "completed" && !p.completedWorkoutId,
                  )}
                >
                  {(w) => {
                    const missed = w.status === "missed";
                    return (
                      <Cell
                        href={`/workouts/planned/${w.id}`}
                        state={withFrom("/")}
                        title={w.title}
                        subtitle={`${w.exercises.length} ${t("EXERCISES").toLowerCase()}${
                          w.startTime ? ` · ${w.startTime}` : ""
                        }`}
                        value={missed ? t("MISSED") : t("PLANNED")}
                        chevron
                      />
                    );
                  }}
                </For>
              </Section>
            </Show>
          )}
        </Show>
      </div>
    </div>
  );
}

function ListIcon() {
  return (
    <svg
      class="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 6h16M4 12h16M4 18h10"
      />
    </svg>
  );
}
